/** Gestão multi-tenant (FASE 2) — /geo/v1/admin/* e /geo/v1/revenda/*. */

import type { Branding } from './me';

export interface TenantSummary extends TenantCadastro {
  id: number;
  parent_id: number | null;
  tipo: string; // revenda|cliente
  nome: string;
  traccar_user_id: number | null;
  status: string; // ativo|suspenso|inadimplente
  plano: string | null;
  device_limit: number;
  user_limit: number;
  nome_exibicao: string | null;
  subdominio: string | null;
  modulos_on: number;
}

export interface TenantUsage {
  tenant_id: number;
  device_count: number;
  user_count: number;
}

export interface Tenant {
  id: number;
  parent_id: number | null;
  tipo: string;
  nome: string;
  traccar_user_id: number | null;
  traccar_group_id: number | null;
  status: string;
  plano: string | null;
  device_limit: number;
  user_limit: number;
  created_at: string | null;
}

/** Ficha cadastral do tenant (cliente/revenda) — seções endereço, dados, financeiro. */
export interface TenantCadastro {
  endereco?: string | null;
  cidade?: string | null;
  uf?: string | null;
  pais?: string | null;
  cep?: string | null;
  cod_interno?: string | null;
  documento?: string | null;
  cpf_cnpj?: string | null;
  email?: string | null;
  telefone?: string | null;
  telefone_urgencia?: string | null;
  responsavel?: string | null;
  valor_plano?: string | null;
  tipo_cobranca?: string | null;
  data_vencimento?: string | null;
  obs_financeira?: string | null;
  obs?: string | null;
}

export interface TenantInput extends TenantCadastro {
  tipo: string;
  nome: string;
  parent_id?: number | null;
  traccar_user_id?: number | null;
  plano?: string | null;
  device_limit?: number;
  user_limit?: number;
  status?: string;
}

export interface ModuleEntitlementAdmin {
  key: string;
  nome: string;
  categoria: string;
  escopo: string;
  habilitado: boolean;
  cota: number | null;
}

export interface TraccarUser {
  id: number;
  name: string;
  email: string | null;
  administrator: boolean;
  userLimit: number | null;
  deviceLimit: number | null;
  disabled: boolean;
}

export interface Cliente {
  id: number;
  nome: string;
  status: string;
  plano: string | null;
  device_limit: number;
  user_limit: number;
  traccar_group_id?: number | null;
}

const BASE = '/geo/v1';

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, {
    credentials: 'include',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  if (!r.ok) {
    let detail = `${r.status}`;
    try {
      const j = await r.json();
      if (j?.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

export const adminApi = {
  // tenants
  listTenants: () => req<TenantSummary[]>(`${BASE}/admin/tenants`),
  tenantsUsage: () => req<TenantUsage[]>(`${BASE}/admin/tenants/usage`),
  createTenant: (body: TenantInput) =>
    req<Tenant>(`${BASE}/admin/tenants`, { method: 'POST', body: JSON.stringify(body) }),
  patchTenant: (id: number, body: Partial<TenantInput>) =>
    req<Tenant>(`${BASE}/admin/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTenant: (id: number) => req<void>(`${BASE}/admin/tenants/${id}`, { method: 'DELETE' }),
  // módulos
  getModules: (id: number) => req<ModuleEntitlementAdmin[]>(`${BASE}/admin/tenants/${id}/modules`),
  setModules: (id: number, modules: { key: string; habilitado: boolean; cota: number | null }[]) =>
    req<ModuleEntitlementAdmin[]>(`${BASE}/admin/tenants/${id}/modules`, {
      method: 'PUT',
      body: JSON.stringify({ modules }),
    }),
  // branding
  getBranding: (id: number) => req<Branding>(`${BASE}/admin/tenants/${id}/branding`),
  setBranding: (id: number, body: Partial<Branding>) =>
    req<Branding>(`${BASE}/admin/tenants/${id}/branding`, { method: 'PUT', body: JSON.stringify(body) }),
  // traccar users (para vincular)
  traccarUsers: () => req<TraccarUser[]>(`${BASE}/admin/traccar-users`),
  // aplicar um plano (bundle de módulos/cotas) ao tenant
  applyPlan: (tenantId: number, planId: number) =>
    req<ModuleEntitlementAdmin[]>(`${BASE}/admin/tenants/${tenantId}/apply-plan`, {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId }),
    }),
  // auditoria de entitlements do tenant
  audit: (tenantId: number) => req<AuditEntry[]>(`${BASE}/admin/tenants/${tenantId}/audit`),
  // financeiro consolidado por revenda
  financeiro: (from?: string, to?: string) => req<AdminFinanceiro>(`${BASE}/admin/financeiro${qs(from, to)}`),
  // papéis/operadores (user_profiles)
  listUserProfiles: () => req<UserProfile[]>(`${BASE}/admin/user-profiles`),
  setUserProfile: (userId: number, papel: string, tenantId?: number | null) =>
    req<UserProfile>(`${BASE}/admin/user-profiles/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ papel, tenant_id: tenantId ?? null }),
    }),
  removeUserProfile: (userId: number) =>
    req<void>(`${BASE}/admin/user-profiles/${userId}`, { method: 'DELETE' }),
  // contador de features pagas liberadas por revenda (ex.: Navegador Premium) — para cobrança
  featureUsage: () => req<FeatureUsageRevenda[]>(`${BASE}/admin/feature-usage`),
};

export interface FeatureUsageUser {
  user_id: number;
  liberado_por: string | null;
  liberado_em: string | null;
}
export interface FeatureUsageFeature {
  key: string;
  nome: string;
  count: number;
  usuarios: FeatureUsageUser[];
}
export interface FeatureUsageRevenda {
  revenda_id: number | null;
  revenda_nome: string;
  total: number;
  features: FeatureUsageFeature[];
}

export interface UserProfile {
  user_id: number;
  tenant_id: number | null;
  papel: string;
}

export interface UserEntitlement {
  user_id: number;
  papel: string;
  tenant_nome: string | null;
  is_admin: boolean;
  funcoes: { key: string; nome: string }[];
}

export interface UserModuleView {
  key: string;
  nome: string;
  categoria: string;
  tenant_on: boolean;
  user_on: boolean | null; // null = herda do tenant
  effective: boolean;
}

/** Funções (módulos) por usuário — /geo/v1/users/* (camada user_modules). */
export const usersEntApi = {
  entitlements: () => req<UserEntitlement[]>(`${BASE}/users/entitlements`),
  getModules: (uid: number) => req<UserModuleView[]>(`${BASE}/users/${uid}/modules`),
  setModules: (uid: number, modules: { key: string; habilitado: boolean }[]) =>
    req<UserModuleView[]>(`${BASE}/users/${uid}/modules`, { method: 'PUT', body: JSON.stringify({ modules }) }),
};

/** Papéis atribuíveis pelo ADMIN (rótulos exibidos na tela de operadores). */
export const PAPEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'operador_suporte', label: 'Operador — Suporte Técnico' },
  { value: 'operador_financeiro', label: 'Operador — Financeiro' },
  { value: 'gestor', label: 'Gestor (cliente)' },
  { value: 'operador', label: 'Operador (cliente)' },
  { value: 'tecnico', label: 'Técnico de instalação' },
  { value: 'central', label: 'Central 24h' },
];

export const PAPEL_LABEL: Record<string, string> = {
  admin: 'Administrador',
  revenda: 'Revenda',
  cliente: 'Cliente final',
  operador_suporte: 'Suporte Técnico',
  operador_financeiro: 'Financeiro',
  gestor: 'Gestor',
  operador: 'Operador',
  tecnico: 'Técnico',
  central: 'Central 24h',
  proprietario: 'Proprietário',
};

export interface AuditEntry {
  id: number;
  ator: string | null;
  acao: string;
  detalhe: Record<string, unknown>;
  em: string | null;
}

export interface PlanSummary {
  id: number;
  nome: string;
  descricao: string | null;
  modulos_on: number;
}

export interface Plan {
  id: number;
  nome: string;
  descricao: string | null;
}

export const plansApi = {
  list: () => req<PlanSummary[]>(`${BASE}/admin/plans`),
  create: (body: { nome: string; descricao?: string | null }) =>
    req<Plan>(`${BASE}/admin/plans`, { method: 'POST', body: JSON.stringify(body) }),
  patch: (id: number, body: { nome?: string; descricao?: string | null }) =>
    req<Plan>(`${BASE}/admin/plans/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: number) => req<void>(`${BASE}/admin/plans/${id}`, { method: 'DELETE' }),
  getModules: (id: number) => req<ModuleEntitlementAdmin[]>(`${BASE}/admin/plans/${id}/modules`),
  setModules: (id: number, modules: { key: string; habilitado: boolean; cota: number | null }[]) =>
    req<ModuleEntitlementAdmin[]>(`${BASE}/admin/plans/${id}/modules`, {
      method: 'PUT',
      body: JSON.stringify({ modules }),
    }),
};

export interface RevendaFinanceiro {
  de: string; ate: string;
  total_receita: number; total_locacoes: number; total_horas: number;
  clientes: { tenant_id: number; nome: string; receita: number; locacoes: number; horas: number }[];
}

export interface AdminFinanceiro {
  de: string; ate: string;
  total_receita: number; total_locacoes: number;
  revendas: { revenda_id: number; nome: string; clientes: number; receita: number; locacoes: number }[];
}

function qs(from?: string, to?: string) {
  const p = new URLSearchParams();
  if (from) p.set('from', from);
  if (to) p.set('to', to);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export interface RevendaClienteInput {
  nome: string;
  traccar_user_id?: number | null;
  traccar_group_id?: number | null;
  device_limit?: number;
  user_limit?: number;
}

export const revendaApi = {
  listClientes: () => req<Cliente[]>(`${BASE}/revenda/clientes`),
  createCliente: (body: RevendaClienteInput) =>
    req<Cliente>(`${BASE}/revenda/clientes`, { method: 'POST', body: JSON.stringify(body) }),
  financeiro: (from?: string, to?: string) => req<RevendaFinanceiro>(`${BASE}/revenda/financeiro${qs(from, to)}`),
  myModules: () => req<ModuleEntitlementAdmin[]>(`${BASE}/revenda/modules`),
  setClientModules: (cid: number, modules: { key: string; habilitado: boolean; cota: number | null }[]) =>
    req<ModuleEntitlementAdmin[]>(`${BASE}/revenda/clientes/${cid}/modules`, {
      method: 'PUT',
      body: JSON.stringify({ modules }),
    }),
};
